import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '../Loader';
import { SubscriptionPlanSelectorV3 } from '../SubscriptionPlanSelectorV3';
import { SubscriptionPlanSelectorV8 } from '../SubscriptionPlanSelectorV8';
import { CheckoutV2 } from '../CheckoutV2';
import { Steps, PLAN_STEP } from '../Steps';
import { Icon } from '../Icon';
import { useSubscriptionPlans } from '../../shared/use-subscription-plans';
import { useIntersectionTracking } from '../../shared/use-intersection-tracking';
import { scrollToPlans } from '../../shared/scroll-helpers';
import { createSubscriptionForm } from '../../../lib/use-cases/create-subscription';
import { handleCreateSubscriptionError } from '../../shared/error-helpers';
import { resolveFirstStepUrl } from '../../../lib/shared/upsell-flow';
import { getCurrentPrelander } from '../../../lib/shared/prelander-helpers';
import { getMetadata } from '../../../lib/shared/ad-helpers';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { getChapterLabel, getChapterCategory } from '../../shared/chapter-answer-mapper';
import { getPlanDiscountPercent } from '../../shared/plan-helpers';
import { US_REGION } from '../../app-legal-regions';
import {
    MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN,
    MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN,
    MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN
} from '../../../lib/shared/subscription-plans';
import styles from './SaleV28.css';

const discountedPlans = [MK_FOUR_WEEKS_2999_FOUR_WEEKS_1999_INTRO_PLAN, MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN, MK_FOUR_WEEKS_2999_ONE_WEEK_899_TRIAL_PLAN];

const CHAPTER_LABEL_MAP = {
    1: 'Growing Up',
    2: 'First Job',
    3: 'A Hard Year',
    4: 'Who Raised You'
};

const CHAPTER_CATEGORY_MAP = {
    1: 'earlyChildhoodAndRoots',
    2: 'earlyAdulthood',
    3: 'maturityAndCareer',
    4: 'earlyChildhoodAndRoots'
};

function SaleV28({
    data,
    initialPrelander,
    spriteVersion,
    selectSubscription,
    selectedSubscriptionData,
    merchantData,
    setMerchantData,
    legalLinks,
    legalEntity,
    legalRegion
}) {
    const isUsRegion = legalRegion === US_REGION;
    const { plans, isLoading } = useSubscriptionPlans([]);
    const [isPending, setIsPending] = useState(false);
    const [activePlanData, setActivePlanData] = useState({});
    const firstUpsellUrl = resolveFirstStepUrl(getCurrentPrelander(), merchantData.subscriptionId);
    const chapterLabel = getChapterLabel(data.userResponses, CHAPTER_LABEL_MAP);

    useIntersectionTracking('.fp-block');

    const handleClose = () => {
        selectSubscription({});
        setMerchantData({});
        scrollToPlans();
    };

    useEffect(() => {
        const handleNavigation = e => {
            if (e.navigationType === 'traverse' && window.history.state?.checkoutVisible) {
                window.history.replaceState({ checkoutVisible: false }, '');
                handleClose();
            }
        };

        logEvent(EVENT_TYPES.SALE_PAGE_VISITED, { chapter: chapterLabel });

        window.navigation.addEventListener('navigate', handleNavigation);
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        return () => {
            window.navigation.removeEventListener('navigate', handleNavigation);
        };
    }, []);

    const getPlans = () => discountedPlans
        .map(plan => plans.find(item => item.plan === plan))
        .filter(Boolean)
        .map(plan => ({
            ...plan,
            isPopular: plan.plan === MK_TWELVE_WEEKS_5999_TWELVE_WEEKS_3499_INTRO_PLAN
        }));

    useEffect(() => {
        const newPlans = getPlans();
        const defaultPlan = newPlans.find(plan => plan.isPopular) ?? newPlans[0];

        if (defaultPlan) {
            setActivePlanData(defaultPlan);
        }
    }, [plans]);

    const submitSubscription = async subscriptionData => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses, CHAPTER_CATEGORY_MAP)
                }
            });

            window.history.pushState({ checkoutVisible: true }, '');
            setMerchantData(newMerchantData);

            if (newMerchantData?.fbEventId) {
                logEvent(EVENT_TYPES.SALE_CHECKOUT_INITIATE, { eventId: newMerchantData.fbEventId });
            }
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const tryAgain = async (subscriptionData, subscriptionId) => {
        try {
            setIsPending(true);

            const newMerchantData = await createSubscriptionForm({
                plan: subscriptionData.plan,
                email: data.email,
                id: subscriptionId,
                metadata: {
                    ...getMetadata(window.location.search, initialPrelander, data.userResponses),
                    memoryInitialChapter: getChapterCategory(data.userResponses, CHAPTER_CATEGORY_MAP)
                }
            });

            setMerchantData(newMerchantData);
        } catch (e) {
            handleCreateSubscriptionError(e);
        } finally {
            setIsPending(false);
        }
    };

    const handleSubscribe = subscriptionData => {
        logEvent(EVENT_TYPES.PLAN_SELECTED, { plan: subscriptionData.plan });
        setMerchantData({});
        selectSubscription(subscriptionData);
        submitSubscription(subscriptionData);
    };

    if (merchantData.merchant && selectedSubscriptionData.plan) {
        return (
            <CheckoutV2
                merchantData={merchantData}
                subscriptionData={selectedSubscriptionData}
                promoType={data.promoType}
                onClose={handleClose}
                tryAgain={tryAgain}
                isPending={isPending}
                successUrl={firstUpsellUrl}
                termsOfUseLink={legalLinks.termsOfUse}
                defaultDiscountValue={getPlanDiscountPercent(selectedSubscriptionData)}
                legalEntity={legalEntity}
                submitLabel={isUsRegion ? 'Subscribe' : 'Pay and Get my plan'}
                isWalletEnabled
            />
        );
    }

    const newPlans = getPlans();
    const PlanSelector = isUsRegion ? SubscriptionPlanSelectorV3 : SubscriptionPlanSelectorV8;

    return (
        <>
            <Steps currentStepId={PLAN_STEP} />
            <div className={styles.coupon}>
                <div className={styles.couponContent}>
                    <Icon name='discountTag' className={styles.couponIcon} />
                    <p className={styles.couponText}>
                        {'The coupon code '}
                        <strong>STORY2026</strong>
                        {' has been applied. '}
                        <strong>Your discount of up to 42% is now active.</strong>
                        {' Enjoy your savings!'}
                    </p>
                </div>
            </div>
            <div className={styles.root}>
                <div id='subscription-start-block' className={styles.subscriptionStartBlock} />
                <div className={styles.planIntro}>
                    <p className={styles.planIntroTitle}>Choose your Digital Memoir</p>
                    <p className={styles.planIntroDescription}>Every plan gives you access to personalized questions across seven life chapters. Answer by voice or text at your own pace, and Boundly will shape your memories into a polished Digital Memoir.</p>
                </div>
                <div id={EVENT_TYPES.FIRST_PLAN_VIEWED} className='fp-block' />
                {isLoading
                    ? <Loader className={styles.loader} variant='secondary' />
                    : (
                        <PlanSelector
                            onSubmit={handleSubscribe}
                            selectedSubscriptionData={activePlanData}
                            selectSubscription={setActivePlanData}
                            isPending={isPending}
                            plans={newPlans}
                            spriteVersion={spriteVersion}
                            termsOfUseLink={legalLinks.termsOfUse}
                        />
                    )
                }
            </div>
        </>
    );
}

SaleV28.propTypes = {
    data: PropTypes.object.isRequired,
    initialPrelander: PropTypes.string.isRequired,
    spriteVersion: PropTypes.string.isRequired,
    legalLinks: PropTypes.object.isRequired,
    legalEntity: PropTypes.object.isRequired,
    legalRegion: PropTypes.string.isRequired
};

export {
    SaleV28
};
